/**
 * @file 後台管理服務 API
 * @description 提供後台管理相關的 API 方法，包含文章 CRUD、分類選項等功能
 */
import { get, post, patch, del } from './sub_services/base';
import apiPath from './api.path';
import config from 'config/config';
import ExceptionHandleService from 'utils/exceptionHandler';

const _EHS = new ExceptionHandleService({
  _NAME: 'services/backStage.ts',
  _NOTICE: 'backStage api'
});

/** 後台文章列表請求參數介面 */
export interface BackStagePostListReq {
  page?: number;
  pageSize?: number;
  [key: string]: unknown;
}

/** 後台指定文章請求參數介面 */
export interface BackStagePostByIdReq {
  postId: string;
  [key: string]: unknown;
}

/** 後台新增文章請求參數介面 */
export interface BackStageAddPostReq {
  postName: string;
  slug: string;
  content: string;
  shortContent: string;
  category: string;
  isPublish: string;
  isPublic: string;
}

/** 後台編輯文章請求參數介面 */
export interface BackStageEditPostReq {
  postId: string;
  postName: string;
  slug?: string; // 僅在補填時使用
  content: string;
  shortContent: string;
  category: string;
  isPublish: string;
  isPublic: string;
}

/** 後台刪除文章請求參數介面 */
export interface BackStageDeletePostReq {
  postId: string;
  [key: string]: unknown;
}

/** 後台分類列表請求參數介面 */
export interface BackStageCategoryListReq {
  page: number;
  perPage: number;
  search?: string;
}

/** 後台新增分類請求參數介面 */
export interface BackStageAddCategoryReq {
  categoryName: string;
}

/** 後台編輯分類請求參數介面 */
export interface BackStageEditCategoryReq {
  categoryId: string;
  categoryName: string;
}

/** 後台檢查分類可否刪除請求參數介面 */
export interface BackStageCheckCategoryDeletableReq {
  categoryId: string;
}

/** 後台刪除分類請求參數介面 */
export interface BackStageDeleteCategoryReq {
  [key: string]: unknown;
  categoryId: string;
}

/** [POST]取得後台文章列表 */
const getBackStagePostList = async (req: BackStagePostListReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStage.getBackStagePostList}`;
    const result = await post(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'getBackStagePostList', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [POST]取得後台指定文章 */
const getBackStagePostById = async (req: BackStagePostByIdReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStage.getBackStagePostById}`;
    const result = await post(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'getBackStagePostById', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [GET]取得後台分類下拉選單 */
const getBackStageCategoryOptions = async (): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStage.getBackStageCategoryOptions}`;
    const result = await get(url);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'getBackStageCategoryOptions', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [POST]後台新增文章 */
const postBackStageAddPost = async (req: BackStageAddPostReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStage.postBackStageAddPost}`;
    const result = await post(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'postBackStageAddPost', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [PATCH]後台編輯文章 */
const patchBackStageEditPost = async (req: BackStageEditPostReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStage.patchBackStageEditPost}`;
    const result = await patch(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'patchBackStageEditPost', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [DELETE]後台刪除文章 */
const deleteBackStagePost = async (req: BackStageDeletePostReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStage.deleteBackStagePost}`;
    const result = await del(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'deleteBackStagePost', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [POST]取得後台分類列表 */
const getBackStageCategoryList = async (req: BackStageCategoryListReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStageCategory.getBackStageCategoryList}`;
    const result = await post(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'getBackStageCategoryList', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [POST]後台新增分類 */
const postBackStageAddCategory = async (req: BackStageAddCategoryReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStageCategory.postBackStageAddCategory}`;
    const result = await post(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'postBackStageAddCategory', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [PATCH]後台編輯分類 */
const patchBackStageEditCategory = async (req: BackStageEditCategoryReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStageCategory.patchBackStageEditCategory}`;
    const result = await patch(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'patchBackStageEditCategory', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [POST]檢查分類是否可刪除 */
const postCheckCategoryDeletable = async (
  req: BackStageCheckCategoryDeletableReq
): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStageCategory.postCheckCategoryDeletable}`;
    const result = await post(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'postCheckCategoryDeletable', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [DELETE]後台刪除分類 */
const deleteBackStageCategory = async (req: BackStageDeleteCategoryReq): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.backStageCategory.deleteBackStageCategory}`;
    const result = await del(url, req);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'deleteBackStageCategory', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

export default {
  getBackStagePostList,
  getBackStagePostById,
  postBackStageAddPost,
  patchBackStageEditPost,
  deleteBackStagePost,
  getBackStageCategoryOptions,
  getBackStageCategoryList,
  postBackStageAddCategory,
  patchBackStageEditCategory,
  postCheckCategoryDeletable,
  deleteBackStageCategory
};
